import React from 'react';
import tinycolor from 'tinycolor2';

export interface LookupStatus {
  /** Stable identifier — used for data-cy hooks and analytics. */
  tag: string;
  /** Human-readable label, comes from the lookup table on the API. */
  name: string;
  /** Hex colour with or without leading '#', e.g. "4CAF50" or "#4CAF50". */
  colour: string;
}

interface ComponentProps {
  status: LookupStatus;
  dataCy?: string;
  /** Optional id — exposed so callers can attach a tooltip portal to the badge. */
  id?: string;
  /** Class(es) appended to the base `.badge` — prefer this over `style` for layout (keeps it in SCSS). */
  className?: string;
  /** Inline styles, merged after the computed background/color so callers can't override the badge
   *  colours. Prefer `className` for layout; use this only for dynamic values. */
  style?: React.CSSProperties;
}

/**
 * Single source of truth for status-badge rendering across the app: a filled
 * Bootstrap `.badge` pill in the API-supplied colour. We deliberately render a
 * plain <span class="badge"> rather than reactstrap's <Badge> — reactstrap
 * defaults to color="secondary", which adds `bg-secondary`, and Bootstrap 5
 * generates that class with `!important`, silently overriding any inline
 * backgroundColor. The base `.badge` class on its own carries no `!important`
 * background, so inline styles win.
 */
const LookupStatusBadge: React.FC<ComponentProps> = ({ status, dataCy, id, className, style }) => {
  const { name, colour } = status;
  const tinyCol = tinycolor(colour);
  return (
    <span
      id={id}
      className={className ? `badge ${className}` : 'badge'}
      style={{ backgroundColor: tinyCol.toHexString(), color: tinyCol.isDark() ? '#fff' : '#000', ...style }}
      data-cy={dataCy}
    >
      {name}
    </span>
  );
};

export default LookupStatusBadge;
